/** Sprint 5-b PWA · 語音建立行事曆事件（mic → STT → AI parse → 確認 → 建立）。 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';

import { useContinuousSpeech } from '../../../hooks/useContinuousSpeech';
import { useMessage } from '../../../contexts/MessageContext';
import { getDisplayTimeZone } from '../../../utils/dateHelper';
import { createEvent } from '../../../services/eeMyCalendarService';
import { voiceParse, VoiceParseResult } from './services/voiceCalendarService';

const VoiceCreatePage: React.FC = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pwaVoice.${k}`, d);
  const { showApiError, showSuccess } = useMessage();
  // 連續 STT：開始/結束都由使用者按，講完不自動停（porsche dogfood 要求）
  const { supported, recording, interim, start, stop } = useContinuousSpeech('zh-TW');

  const [transcript, setTranscript] = useState('');
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState<VoiceParseResult | null>(null);
  const [creating, setCreating] = useState(false);

  // final 段落持續 append 到 transcript（停頓再講也接續）
  const handleStart = () => start((finalText) =>
    setTranscript((prev) => (prev ? `${prev} ${finalText}` : finalText).trim()));

  const parse = async () => {
    if (!transcript.trim()) return;
    setParsing(true); setResult(null);
    try { setResult(await voiceParse(transcript.trim(), getDisplayTimeZone())); }
    catch (err) { showApiError(err); }
    finally { setParsing(false); }
  };

  const create = async () => {
    if (!result) return;
    const ef = result.event_fields;
    if (!ef.summary || !ef.dtstart) {
      showApiError({ response: { data: { detail: 'ERR430001' } } }); return;
    }
    setCreating(true);
    try {
      const ev = await createEvent({
        summary: ef.summary, dtstart: ef.dtstart, dtend: ef.dtend || undefined,
        location: ef.location || undefined, description: ef.description || undefined,
        rrule: ef.rrule || undefined,
      });
      showSuccess('SYS020001', { name: ef.summary });
      // 會議類型 → 直接進會議室；其餘回行事曆
      nav(ef.event_type === 'meeting' ? `/ee_pwa_meetings/${ev.id}` : '/ee_pwa_calendar');
    } catch (err) { showApiError(err); }
    finally { setCreating(false); }
  };

  const ef = result?.event_fields;
  return (
    <Box sx={{ p: 2, maxWidth: 560, mx: 'auto' }}>
      <Button size="small" onClick={() => nav('/ee_pwa_calendar')}>← {tt('back', '返回行事曆')}</Button>
      <Typography variant="h6" sx={{ my: 1 }}>🎙️ {tt('title', '語音建立行程')}</Typography>

      {!supported && <Alert severity="warning" sx={{ mb: 1 }}>
        {tt('noStt', '此瀏覽器不支援語音辨識，請直接輸入文字。')}</Alert>}

      <Stack alignItems="center" spacing={1} sx={{ my: 2 }}>
        <Button variant="contained" color={recording ? 'error' : 'primary'}
          onClick={recording ? stop : handleStart} disabled={!supported}
          sx={{
            width: 96, height: 96, borderRadius: '50%',
            animation: recording ? 'pwaPulse 1.4s ease-out infinite' : 'none',
          }}>
          {recording ? <StopIcon sx={{ fontSize: 40 }} /> : <MicIcon sx={{ fontSize: 40 }} />}
        </Button>
        <Typography variant="caption" color="text.secondary" align="center">
          {recording
            ? tt('recording', '錄音中…再按一次結束（開始/結束都由你按）')
            : tt('tapMic', '按麥克風開始說話（例：明天下午三點跟客戶開會）')}</Typography>
        {recording && interim && (
          <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic' }}>{interim}</Typography>
        )}
      </Stack>
      <style>{'@keyframes pwaPulse{0%{box-shadow:0 0 0 0 rgba(211,47,47,.5)}70%{box-shadow:0 0 0 16px rgba(211,47,47,0)}100%{box-shadow:0 0 0 0 rgba(211,47,47,0)}}'}</style>

      <TextField fullWidth multiline minRows={2} size="small" label={tt('transcript', '語音內容')}
        value={transcript} onChange={(e) => setTranscript(e.target.value)} sx={{ mb: 1 }} />
      <Button variant="outlined" fullWidth onClick={parse} disabled={!transcript.trim() || parsing}>
        {parsing ? <CircularProgress size={18} /> : tt('parse', 'AI 解析')}</Button>

      {ef && (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>✅ {tt('confirm', '請確認')}</Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2"><strong>{tt('summary', '標題')}：</strong>{ef.summary || '—'}</Typography>
            <Typography variant="body2"><strong>{tt('start', '開始')}：</strong>{ef.dtstart || tt('missing', '（未指定）')}</Typography>
            <Typography variant="body2"><strong>{tt('end', '結束')}：</strong>{ef.dtend || '—'}</Typography>
            {ef.location && <Typography variant="body2"><strong>{tt('loc', '地點')}：</strong>{ef.location}</Typography>}
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
              <Chip size="small" label={tt(`type_${ef.event_type}`, ef.event_type)} />
              {ef.rrule && <Chip size="small" variant="outlined" label={ef.rrule} />}
              {ef.attendee_user_ids.length > 0 &&
                <Chip size="small" color="info" label={`${tt('attendees', '出席者')} ${ef.attendee_user_ids.length}`} />}
            </Stack>
          </Stack>
          {result!.missing_fields.length > 0 && <Alert severity="warning" sx={{ mt: 1 }}>
            {tt('missingHint', '缺少欄位')}：{result!.missing_fields.join('、')} — {tt('reSpeak', '可補講或手動填')}</Alert>}
          {result!.ambiguous_attendees.length > 0 && <Alert severity="info" sx={{ mt: 1 }}>
            {tt('ambiguous', '有同名出席者需確認')}：{result!.ambiguous_attendees.map((a) => a.name).join('、')}</Alert>}
          {result!.unmatched_attendees.length > 0 && <Alert severity="info" sx={{ mt: 1 }}>
            {tt('unmatched', '找不到帳號（需先加入租戶）')}：{result!.unmatched_attendees.join('、')}</Alert>}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {tt('cost', '本次解析')}：{result!.cost.tokens} tokens · NT${result!.cost.ntd.toFixed(2)}</Typography>
          <Button variant="contained" fullWidth sx={{ mt: 1 }} onClick={create}
            disabled={creating || !ef.summary || !ef.dtstart}>
            {creating ? <CircularProgress size={18} /> : tt('createBtn', '確認建立')}</Button>
        </Paper>
      )}
    </Box>
  );
};

export default VoiceCreatePage;
